import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { MsalService } from '@azure/msal-angular';

import { LoginI, LoginResponseI, DecodedTokenI } from './authentication.interface';
import { environment } from '../../../environments/environment';
import { CLEAR_USER, SET_USER } from './user.actions';
import { UserStoreI } from '../../shared/models/States/user-store.interface';
import { RouteHandlerService } from '../services/route-handler.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly urlAuth = environment.baseUrl.auth;
  private readonly tokenKey = 'token';
  private readonly refreshTokenKey = 'refreshToken';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private http = inject(HttpClient);
  private cookie = inject(CookieService);
  private router = inject(Router);
  private store = inject(Store<{ user: UserStoreI }>);
  private msalService = inject(MsalService);
  private routeHandler = inject(RouteHandlerService);

  constructor() {
    this.checkAuthenticationStatus();
  }

  // Check whether there is a valid session (including MSAL)
  async checkSessionValidity(): Promise<boolean> {
    // First, check traditional token
    const token = this.getToken();
    if (token) {
      return true;
    }

    // If there is no token, check MSAL session
    try {
      const account = await this.msalService.instance.getActiveAccount();
      if (account) {
        return true;
      }

      // Check cached accounts
      const accounts = await this.msalService.instance.getAllAccounts();
      return accounts.length > 0;
    } catch {
      return false;
    }
  }

  // Restore session from MSAL if available
  async restoreSessionFromMsal(): Promise<boolean> {
    try {
      const account = await this.msalService.instance.getActiveAccount();
      if (!account) {
        const accounts = await this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          await this.msalService.instance.setActiveAccount(accounts[0]);
        } else {
          return false;
        }
      }

      const response = await this.msalService.instance.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: account || (await this.msalService.instance.getActiveAccount())
      });

      // Optionally, exchange MSAL token with your backend for an app token
      this.setCookie(this.tokenKey, response.accessToken);
      this.isAuthenticatedSubject.next(true);

      return true;
    } catch {
      // console.error('Error restoring session from MSAL:', error);
      return false;
    }
  }

  login(credentials: LoginI): Observable<LoginResponseI> {
    return this.http.post<LoginResponseI>(`${this.urlAuth}Acces/login`, credentials).pipe(
      tap(response => {
        this.handleSuccessfulLogin(response);
      }),
      catchError(error => {
        return this.handleError(error);
      })
    );
  }

  b2cLogin(credentials: LoginI): Observable<LoginResponseI> {
    return this.http.post<LoginResponseI>(`${this.urlAuth}Acces/SingUp`, credentials).pipe(
      tap(response => this.handleSuccessfulLogin(response)),
      catchError(this.handleError.bind(this))
    );
  }

  logout(): void {
    this.clearSession();
    this.routeHandler.clearLastRoute();
    this.router.navigateByUrl('/login');
  }

  clearSession(): void {
    this.cookie.deleteAll();
    localStorage.clear();
    sessionStorage.clear();
    this.isAuthenticatedSubject.next(false);
    this.store.dispatch(CLEAR_USER());
  }

  getCookie(name: string): string {
    return this.cookie.get(name);
  }

  setCookie(name: string, value: string, expires?: Date): void {
    if (expires) {
      this.cookie.set(name, value, expires);
    } else {
      this.cookie.set(name, value);
    }
    try {
      localStorage.setItem(name, value);
    } catch {
      return;
    }
  }

  getToken(): string | null {
    let token = this.getCookie(this.tokenKey);
    if (!token) {
      try {
        token = localStorage.getItem(this.tokenKey) || '';
      } catch {
        token = '';
      }
    }
    return token && this.isValidToken(token) ? token : null;
  }

  getRefreshToken(): string | null {
    return this.getCookie(this.refreshTokenKey);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  getUserRole(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = this.decodeToken(token);
      return decoded?.role || null;
    } catch {
      return null;
    }
  }

  getUserId(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = this.decodeToken(token);
      return decoded?.userId || null;
    } catch {
      return null;
    }
  }

  getUserName(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const decoded = this.decodeToken(token);
      return decoded?.name || null;
    } catch {
      return null;
    }
  }

  private handleSuccessfulLogin(response: LoginResponseI): void {
    this.setCookie(this.tokenKey, response.accessToken);
    this.setCookie(this.refreshTokenKey, response.refreshToken || '');

    this.isAuthenticatedSubject.next(true);

    const decoded = this.decodeToken(response.accessToken);
    if (decoded) {
      this.store.dispatch(
        SET_USER({
          user: {
            user_ID: parseInt(decoded.userId) || 0,
            rol: decoded.role || '',
            nombre: decoded.name || '',
            email: decoded.email || '',
            isActive: true
          }
        })
      );
    }

    // Navigate to the last authenticated route or default route
    this.routeHandler.handleSuccessfulAuth();
  }

  private checkAuthenticationStatus(): void {
    const token = this.getToken();
    this.isAuthenticatedSubject.next(!!token);
  }

  private isValidToken(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) {
        // If we cannot decode, assume valid to avoid blocking navigation (dev-friendly)
        return true;
      }

      const exp = Number((decoded as { exp?: number }).exp);
      if (!exp || Number.isNaN(exp)) {
        // If no exp is present, consider token usable
        return true;
      }

      const clockSkewSeconds = 60; // tolerate small clock differences
      const currentTime = Math.floor(Date.now() / 1000) - clockSkewSeconds;
      return exp > currentTime;
    } catch {
      return true;
    }
  }

  decodeToken(token: string): DecodedTokenI | null {
    try {
      return jwtDecode<DecodedTokenI>(token);
    } catch {
      return null;
    }
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'Authentication failed';

    if (error.error && typeof error.error === 'object' && 'message' in error.error) {
      errorMessage = `Client Error: ${(error.error as { message: string }).message}`;
    } else {
      switch (error.status) {
        case 401:
          errorMessage = 'Invalid credentials';
          break;
        case 403:
          errorMessage = 'Access denied';
          break;
        case 404:
          errorMessage = 'Authentication service not found';
          break;
        case 500:
          errorMessage = 'Internal server error';
          break;
        default:
          errorMessage = `Server Error: ${error.status}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }
}
